import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import Logo from "../components/Logo";
import { CheckCircle, Circle, Bug, Lightbulb, Question, ArrowRight, Rocket, Sparkle } from "@phosphor-icons/react";

// SEO — public page, indexable
function useSeoHead(title, description) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", description);
    return () => {
      document.title = prev;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
    };
  }, [title, description]);
}

const TYPE_META = {
  bug:     { icon: Bug,       label: "Bug fix", cls: "text-rose-300 border-rose-500/30 bg-rose-500/5" },
  idea:    { icon: Lightbulb, label: "Feature", cls: "text-amber-300 border-amber-500/30 bg-amber-500/5" },
  general: { icon: Question,  label: "Update",  cls: "text-sky-300 border-sky-500/30 bg-sky-500/5" },
};

function TypeTag({ type }) {
  const t = TYPE_META[type] || TYPE_META.general;
  const Icon = t.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase font-semibold ${t.cls}`}>
      <Icon size={10} weight="fill" />
      {t.label}
    </span>
  );
}

export default function RoadmapPage() {
  useSeoHead(
    "Product Roadmap | FreightPilotAI",
    "See what's shipping in FreightPilotAI — feature requests, bug fixes, and improvements from our pilot fleets.",
  );

  const [data, setData] = useState({ shipped: [], next_up: [], counts: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/roadmap");
        setData(data);
      } catch {
        // silent — empty state below covers it
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="roadmap-page">
      {/* Slim public nav */}
      <header className="border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="min-w-0"><Logo /></Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="text-neutral-300 hover:text-white">Home</Link>
            <Link
              to="/signup"
              data-testid="roadmap-cta-signup"
              className="btn-primary rounded-full px-4 py-2 text-sm inline-flex items-center gap-2"
            >
              Get free assessment
              <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        {/* Hero */}
        <div className="mb-16 sm:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/25 bg-[#FF5A1F]/5 text-[11px] uppercase tracking-widest text-[#FF9764] mb-5">
            <Sparkle size={12} weight="fill" /> Public roadmap
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-5">
            Built with our{" "}
            <span className="text-[#FF5A1F]">pilots</span>, in the open.
          </h1>
          <p className="text-neutral-300 text-lg max-w-2xl leading-relaxed">
            Every idea and bug fix here came from a real trucking company using
            FreightPilotAI. This is what we're shipping, what's next, and how the
            product actually gets better.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-neutral-500 py-16">Loading…</div>
        ) : (
          <>
            {/* Shipped */}
            <RoadmapSection
              icon={CheckCircle}
              iconWeight="fill"
              iconClass="text-emerald-400"
              overline="RECENTLY SHIPPED"
              title="Live and running for pilots today"
              count={data.counts?.shipped || 0}
              items={data.shipped}
              emptyText="Shipped items will appear here as they roll out."
              testid="roadmap-shipped"
              accent="emerald"
            />

            {/* Next up */}
            <RoadmapSection
              icon={Circle}
              iconWeight="regular"
              iconClass="text-[#FF9764]"
              overline="NEXT UP"
              title="On deck, in the works"
              count={data.counts?.next_up || 0}
              items={data.next_up}
              emptyText="We're always taking new pilot ideas — nothing under review right now."
              testid="roadmap-next-up"
              accent="orange"
            />
          </>
        )}

        {/* CTA */}
        <div className="mt-20 sm:mt-28 rounded-2xl border border-[#FF5A1F]/25 bg-gradient-to-br from-[#FF5A1F]/10 via-transparent to-transparent p-8 sm:p-12 text-center">
          <Rocket size={28} weight="fill" className="text-[#FF5A1F] mx-auto mb-4" />
          <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-3">
            Have an idea that would save your team hours?
          </h2>
          <p className="text-neutral-400 max-w-lg mx-auto mb-6">
            Talk to us. Pilot fleets who share their workflow shape what we build next.
          </p>
          <Link
            to="/signup"
            data-testid="roadmap-cta-primary"
            className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2"
          >
            Get your free AI assessment
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Freight Pilot AI LLC</div>
          <a href="mailto:contact@freightpilotai.ai" className="hover:text-[#FF5A1F]">contact@freightpilotai.ai</a>
        </div>
      </footer>
    </div>
  );
}

function RoadmapSection({ icon: Icon, iconWeight, iconClass, overline, title, count, items, emptyText, testid, accent }) {
  const borderAccent = accent === "emerald" ? "border-emerald-500/20" : "border-[#FF5A1F]/25";
  return (
    <section data-testid={testid} className="mb-14 sm:mb-16">
      <div className="flex items-center gap-3 mb-6 sm:mb-8">
        <Icon size={22} weight={iconWeight} className={iconClass} />
        <div className="flex-1 min-w-0">
          <div className="overline text-neutral-400">{overline}</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            {title}
          </h2>
        </div>
        <div className={`text-xs font-mono px-3 py-1 rounded-full border ${borderAccent} text-neutral-300`}>
          {count} {count === 1 ? "item" : "items"}
        </div>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-sm text-neutral-500">
          {emptyText}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <article
              key={it.id}
              data-testid={`roadmap-item-${it.id}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/25 transition-colors"
            >
              <div className="flex items-start gap-3 mb-2">
                <TypeTag type={it.feedback_type} />
                {it.from_drivers && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase font-semibold text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
                        data-testid={`roadmap-votes-${it.id}`}>
                    🗳 asked by {it.votes} driver{it.votes === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <p className="text-neutral-200 text-sm sm:text-base leading-relaxed">
                {it.message}
              </p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
